import { IconButton, Typography } from "@mui/material";
import React, { useContext, useEffect, useRef } from "react";
import stateContext from "../../Context/StateContext";
import { apiClient } from "../../api/client";
import { useQuery } from "@tanstack/react-query";
import { fetchFontFile } from "../../utils/commonUtils.js/commonFunc";
import _ from "lodash";

const FontStyle = ({ activeFontIndex, setActiveFontIndex, inputText }) => {
    const globalState = useContext(stateContext);
    const hasSetFontsRef = useRef(false); // prevent infinite loop

    const fontDataQuery = useQuery({
        queryKey: ["fontData"],
        queryFn: () => apiClient.get("/fonts"),
    });

    const processedFonts = React.useMemo(() => {
        if (
            fontDataQuery.isSuccess &&
            fontDataQuery.data &&
            Array.isArray(fontDataQuery.data.data)
        ) {
            let valueCounter = 1;
            let arabicValueCounter = 1;
            const fontMap = new Map();
            fontDataQuery.data.data.forEach((font) => {
                const existing = fontMap.get(font.id) || {
                    id: font.id,
                    name: font.fontName,
                    arabicName: font.arabicName,
                    language: font.language,
                    value: "",
                    url: "",
                    style: "",
                    arabicStyle: "",
                    arabicUrl: "",
                    arabicValue: "",
                    status: font.status,
                    diamondPositions: font.position,
                    deviation: font.deviation,
                    smallDeviation: font.smallFontDeviation,
                    largeDeviation: font.largeFontDeviation,
                    fontSizes: font.sizeOptions,
                    attachmentSize: font.diamondTypes,
                };

                if (font.language === "English") {
                    existing.value = String(valueCounter++);
                    existing.url = font.fontFileKey;
                    existing.style = font.fontName
                        .replace(/\s+/g, "")
                        .toLowerCase();
                } else if (font.language === "Arabic") {
                    existing.arabicValue = String(arabicValueCounter++);
                    existing.arabicUrl = font.fontFileKey;
                    existing.arabicStyle =
                        "arabic" +
                        font.fontName.replace(/\s+/g, "").toLowerCase();
                }

                fontMap.set(font.id, existing);
            });

            return Array.from(fontMap.values());
        }

        return [];
    }, [fontDataQuery.data, fontDataQuery.isSuccess]);

    // Main font handling effect
    useEffect(() => {
        const handleFontEffect = async () => {
            if (processedFonts.length === 0 || hasSetFontsRef.current) return;

            // Prevent re-updating global fonts if already same
            if (!_.isEqual(globalState.fonts, processedFonts)) {
                globalState.setFonts(processedFonts);
                hasSetFontsRef.current = true;
            } else {
                return;
            }

            // Get first English font (with url)
            const firstEnglishFont = processedFonts.find((font) => font.url);
            globalState.setPriceDeviation(firstEnglishFont.deviation);
            globalState.setDeviationData({
                smallDeviation: firstEnglishFont.smallDeviation,
                mediumDeviation: firstEnglishFont.deviation,
                largeDeviation: firstEnglishFont.largeDeviation,
            });

            // Get first Arabic font (with arabicUrl)
            const firstArabicFont = processedFonts.find(
                (font) => font.arabicUrl
            );

            // globalState.setSelectedFontData(firstEnglishFont);

            try {
                const [englishBuffer, arabicBuffer] = await Promise.all([
                    firstEnglishFont?.url
                        ? fetchFontFile(firstEnglishFont.url)
                        : null,
                    firstArabicFont?.arabicUrl
                        ? fetchFontFile(firstArabicFont.arabicUrl)
                        : null,
                ]);

                // Save both buffers in a structured object
                globalState.setFirstFontBuffers({
                    english: englishBuffer,
                    arabic: arabicBuffer,
                });
                // Select the buffer based on current language
                const selectedBuffer =
                    globalState.selectedFontLanguage === "English"
                        ? englishBuffer
                        : arabicBuffer;
                // if (globalState.selectedFontLanguage === "English") {
                //     globalState.setSelectedFontData(firstEnglishFont);
                // } else {
                globalState.setSelectedFontData(firstArabicFont);
                // }
                if (selectedBuffer) {
                    globalState.setSelectedFont(selectedBuffer);
                }
            } catch (err) {
                console.error("Error fetching font buffers:", err);
            }

            await loadFonts(processedFonts);
        };

        handleFontEffect();
    }, [processedFonts, globalState.selectedFontLanguage, globalState]);

    const loadFonts = async (fontsToLoad) => {
        for (const font of fontsToLoad) {
            try {
                if (font.url) {
                    const buffer = await fetchFontFile(font.url);
                    const fontFace = new FontFace(font.style, buffer);
                    await fontFace.load();
                    document.fonts.add(fontFace);
                }

                if (font.arabicUrl) {
                    const arabicBuffer = await fetchFontFile(font.arabicUrl);
                    const arabicFontFace = new FontFace(
                        font.arabicStyle,
                        arabicBuffer
                    );
                    await arabicFontFace.load();
                    document.fonts.add(arabicFontFace);
                }
            } catch (error) {
                console.error(`Error loading font "${font.name}"`, error);
            }
        }
    };

    const handleFontButtonClick = async (index, font) => {
        setActiveFontIndex(index);
        try {
            let fileBuffer;

            if (globalState.selectedFontLanguage === "English") {
                globalState.setFontNo(font.style);
                globalState.setFontSerialNo(font.value);
                fileBuffer = await fetchFontFile(font.url);
            } else {
                globalState.setFontNo(font.arabicStyle);
                globalState.setFontSerialNo(font.arabicValue);
                fileBuffer = await fetchFontFile(font.arabicUrl);
            }
            globalState.setPriceDeviation(font.deviation);
            globalState.setDeviationData({
                smallDeviation: font.smallDeviation,
                mediumDeviation: font.deviation,
                largeDeviation: font.largeDeviation,
            });
            globalState.setSelectedFont(fileBuffer);
            globalState.setSelectedFontData(font);
        } catch (err) {
            console.error("Error fetching selected font", err);
        }
    };

    return (
        <>
            <div className="tabpanel__content-boxes tabpanel__font-boxes">
                {processedFonts
                    .filter((font) =>
                        globalState.selectedFontLanguage === "English"
                            ? font.language === "English"
                            : font.language === "Arabic"
                    )
                    .map((font, index) => {
                        const isAvailable = font.status === "Available";
                        const displayText =
                            globalState.selectedFontLanguage === "English"
                                ? font.name
                                : font.arabicName
                                ? font.arabicName
                                : font.name;
                        const displayStyle =
                            globalState.selectedFontLanguage === "English"
                                ? font.style
                                : font.arabicStyle;

                        return (
                            <div
                                key={index}
                                className="tabpanel__content-box tabpanel__font-box"
                            >
                                <IconButton
                                    className={`inheritMuiIconButton ${
                                        activeFontIndex === index
                                            ? "active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleFontButtonClick(index, font)
                                    }
                                    disabled={!isAvailable}
                                >
                                    <span className="inheritMuiIconButton-container">
                                        <span className="tabpanel__content-box-label tabpanel__content-box-name">
                                            {displayText}
                                        </span>
                                        <Typography
                                            variant="h4"
                                            className={`tabpanel__content-box-text ${displayStyle}`}
                                            style={{
                                                fontFamily: displayStyle,
                                                userSelect: "none",
                                            }}
                                        >
                                            {inputText}
                                        </Typography>

                                        {!isAvailable && (
                                            <Typography
                                                variant="caption"
                                                style={{
                                                    fontSize: "0.75rem",
                                                    opacity: 0.6,
                                                }}
                                            >
                                                {font.status.replace(/_/g, " ")}
                                            </Typography>
                                        )}
                                    </span>
                                </IconButton>
                            </div>
                        );
                    })}
            </div>
        </>
    );
};

export default FontStyle;
